import { useCallback, useEffect, useRef, useState } from 'react'
import { aiApi } from '../api/aiApi'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { toast } from '@/lib/toast'
import type { NewsItem } from '../types/news.types'

/**
 * Re-read the credit counter from the server after a summary settles.
 *
 * `getState()` rather than a hook: this fires from stream callbacks, not from render.
 * Both endings need it — a completed stream spent a credit, and a failed one had it
 * refunded server-side (`api/v1/ai.py`), so the displayed number is wrong either way
 * until it is re-read. Deriving it locally instead would have to model the refund
 * rule, and would drift the first time that rule changes.
 */
const refreshCredits = () => {
  void useAuthStore.getState().refreshPreferences()
}

/**
 * Say what went wrong, then re-read the counter.
 *
 * `error` was set and never shown: `MainContent` renders `AISummaryCard`, which
 * returns `null` when there is no summary, so every stream failure — a spent
 * allowance most of all — was a spinner that stopped and nothing else. This path is
 * fetch, so the axios interceptor that toasts every other API error never sees it.
 */
const reportFailure = (err: Error) => {
  toast.error('AI 요약', err.message)
  refreshCredits()
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Streaming Summary Hook with Abort Support
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface UseStreamingSummaryParams {
  keyword: string
  articles: NewsItem[]
}

/**
 * Return value of {@link useStreamingSummary}.
 *
 * State lives in the hook, so every call site gets its own isolated copy. Call it
 * once (App) and pass this object down — calling it again in a child produces a
 * second instance that never sees the first one's summaries.
 */
export type StreamingSummary = ReturnType<typeof useStreamingSummary>

export function useStreamingSummary() {
  // Store summaries per keyword to preserve content when switching tabs
  const [summaries, setSummaries] = useState<Record<string, string>>({})
  const [streamingKeyword, setStreamingKeyword] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const startStreaming = useCallback(async ({ keyword, articles }: UseStreamingSummaryParams) => {
    // Reset state for this keyword
    setStreamingKeyword(keyword)
    setSummaries((prev) => ({ ...prev, [keyword]: '' }))
    setError(null)

    // Transform NewsItem to API format
    const articlesForAPI = articles.map((article) => ({
      title: article.title,
      description: article.description,
      pub_date: article.pub_date,
    }))

    try {
      // Start streaming
      const controller = await aiApi.streamSummarizeArticles(
        {
          keyword,
          articles: articlesForAPI,
        },
        // onChunk: append chunk to summary for this keyword
        (chunk) => {
          setSummaries((prev) => ({
            ...prev,
            [keyword]: (prev[keyword] || '') + chunk,
          }))
        },
        // onComplete: mark as done
        () => {
          setStreamingKeyword(null)
          refreshCredits()
        },
        // onError: set error and mark as done
        (err) => {
          setError(err)
          setStreamingKeyword(null)
          reportFailure(err)
        }
      )

      // Store controller for cancellation
      abortControllerRef.current = controller
    } catch (err) {
      const failure = err instanceof Error ? err : new Error('Streaming failed')
      setError(failure)
      setStreamingKeyword(null)
      reportFailure(failure)
    }
  }, [])

  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setStreamingKeyword(null)
    }
  }, [])

  // Get summary for a specific keyword
  const getSummary = useCallback((keyword: string) => summaries[keyword] || '', [summaries])

  // Check if a specific keyword is currently streaming
  const isStreaming = useCallback(
    (keyword: string) => streamingKeyword === keyword,
    [streamingKeyword]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    getSummary,
    isStreaming,
    error,
    startStreaming,
    cancelStreaming,
  }
}
